/**
 * CurrentUserAvatar Component
 *
 * CRITICAL: Use this component EVERYWHERE you need to show the logged-in user's avatar
 *
 * This is the ONLY component that should be used for the current user's avatar.
 * It automatically fetches and displays the user's photo from Firestore.
 *
 * DO NOT:
 * - Create duplicate avatar components for the logged-in user
 * - Pass photoURL manually from Firestore
 * - Fetch user data in other components just for the avatar
 *
 * ALWAYS use this component when showing the current user's avatar in:
 * - Navigation bars
 * - Headers
 * - Profile dropdowns
 * - User menus
 *
 * Example usage:
 * <CurrentUserAvatar size="small" />
 * <CurrentUserAvatar size="md" />
 */

import Avatar from './Avatar';
import { useAuth } from '../../features/auth/hooks/useAuth';
import { useRoleSwitcher } from '../../features/auth/hooks/useRoleSwitcher';

const CurrentUserAvatar = ({ size = 'md', className = '' }) => {
  const { user } = useAuth();
  const { userPermissions } = useRoleSwitcher();

  // userPermissions contains the full Firestore user document including photoURL
  // Fallback to user.photoURL from Firebase Auth if Firestore hasn't loaded yet
  const displayName = userPermissions?.displayName || user?.displayName || '';
  const photoURL = userPermissions?.photoURL || user?.photoURL;

  // For email signups: displayName is null, use firstName/lastName from Firestore
  let firstName = userPermissions?.firstName || displayName.split(' ')[0] || '';
  let surname = userPermissions?.lastName || displayName.split(' ').slice(1).join(' ') || '';

  // Final fallback: first letter of email so we never render a blank avatar
  // (fresh signups have no name on the user doc until onboarding Step 1)
  if (!firstName && !surname) {
    const email = userPermissions?.email || user?.email || '';
    firstName = email.charAt(0) || '';
  }

  return (
    <Avatar
      firstName={firstName}
      surname={surname}
      photoURL={photoURL}
      userId={user?.uid}
      size={size}
      className={className}
    />
  );
};

export default CurrentUserAvatar;
